<template>
    <div class="page missions-reviews-page">
        <div class="page-header">
            <h1>審核列表</h1>
            <p>查看並管理任務條件的待審核記錄</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <a-collapse
                    v-model:activeKey="filterCollapseActiveKey"
                    :bordered="false"
                    class="filter-collapse"
                >
                    <a-collapse-panel key="filters" :showArrow="false">
                        <template #header>
                            <div class="filter-header">
                                <span class="filter-header-title">
                                    <FilterOutlined />
                                    篩選條件
                                </span>
                                <a-badge
                                    :count="getActiveFilterCount()"
                                    :number-style="{
                                        backgroundColor: '#2a4a7a'
                                    }"
                                />
                            </div>
                        </template>

                        <div class="filters-content">
                            <!-- 基本篩選 -->
                            <div class="filter-group">
                                <div class="filter-group-title">基本篩選</div>
                                <div class="filter-row">
                                    <a-select
                                        v-model:value="filters.user_id"
                                        show-search
                                        :filter-option="false"
                                        :options="userOptions"
                                        :loading="userLoading"
                                        placeholder="搜尋會員"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[300px]"
                                        @search="onUserSearch"
                                        @change="onUserChange"
                                        @clear="onUserClear"
                                    >
                                        <template #option="{ label, value }">
                                            <div class="user-option">
                                                <span class="user-name">
                                                    {{ label }}
                                                </span>
                                                <span class="user-id">
                                                    #{{ value }}
                                                </span>
                                            </div>
                                        </template>
                                    </a-select>
                                    <a-select
                                        v-model:value="filters.condition_type"
                                        placeholder="條件類型"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="conditionTypeOptions"
                                    />
                                    <a-select
                                        v-model:value="filters.result_status"
                                        placeholder="審核狀態"
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="statusOptions"
                                    />
                                    <a-select
                                        v-model:value="filters.rank_id"
                                        placeholder="聘階"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="rankOptions"
                                        :loading="rankLoading"
                                    />
                                    <a-select
                                        v-model:value="filters.department_id"
                                        placeholder="事業部"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="departmentOptions"
                                        :loading="departmentLoading"
                                    />
                                </div>
                            </div>

                            <!-- 任務樹篩選 -->
                            <div class="filter-group">
                                <div class="filter-group-title">任務樹篩選</div>
                                <div class="filter-row">
                                    <a-select
                                        v-model:value="nodePath.missionId"
                                        placeholder="主線"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="missionSelectOptions"
                                        :loading="loadingMissions"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleMissionChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.chapterId"
                                        placeholder="章節"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="chapterSelectOptions"
                                        :loading="loadingChapters"
                                        :disabled="!nodePath.missionId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleChapterChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.taskId"
                                        placeholder="任務"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="taskSelectOptions"
                                        :loading="loadingTasks"
                                        :disabled="!nodePath.chapterId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleTaskChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.stepId"
                                        placeholder="步驟"
                                        allow-clear
                                        size="middle"
                                        class="status-select min-w-[160px]"
                                        :options="stepSelectOptions"
                                        :loading="loadingSteps"
                                        :disabled="!nodePath.taskId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleStepChange"
                                        @clear="handleNodePathClear"
                                    />
                                </div>
                            </div>
                        </div>
                    </a-collapse-panel>
                </a-collapse>

                <!-- 操作按鈕區 -->
                <div v-if="hasAnyActionPermission" class="toolbar-actions">
                    <a-space>
                        <a-button
                            v-if="
                                selectedRowKeys.length > 0 &&
                                canBatchReview &&
                                canApproveReview
                            "
                            class="vaporwave-btn batch-approve-btn"
                            :loading="batchReviewLoading"
                            @click="handleBatchApprove"
                        >
                            <CheckCircleOutlined />
                            批次通過 ({{ selectedRowKeys.length }})
                        </a-button>
                        <a-button
                            v-if="
                                selectedRowKeys.length > 0 &&
                                canBatchReview &&
                                canRejectReview
                            "
                            class="vaporwave-btn batch-reject-btn"
                            :loading="batchReviewLoading"
                            @click="handleBatchReject"
                        >
                            <CloseCircleOutlined />
                            批次拒絕 ({{ selectedRowKeys.length }})
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-search-btn"
                            @click="handleSearch"
                        >
                            查詢
                        </a-button>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 審核列表表格 -->
            <DataTable
                :showIndex="true"
                :rowSelection="{
                    selectedRowKeys: selectedRowKeys,
                    onChange: handleSelectionChange,
                    getCheckboxProps: (record) => {
                        // completed 和 failed 狀態不可勾選，其他都可以
                        const cannotSelect =
                            record.result_status === 'completed' ||
                            record.result_status === 'failed';
                        return {
                            disabled: cannotSelect
                        };
                    }
                }"
                title=""
                :columns="columns"
                :data-source="reviewList"
                :pagination="pagination"
                :loading="loading"
                @refresh="loadReviews"
                @change="handleTableChange"
                class="points-table"
            >
                <template #bodyCell="{ column, record }">
                    <template v-if="column.key === 'user_name'">
                        <div class="user-cell">
                            <div
                                class="user-name clickable"
                                @click="handleUserNameClick(record)"
                            >
                                {{ record.user?.name || '-' }}
                            </div>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'user_email'">
                        <div class="user-cell">
                            <span class="user-email">
                                {{ record.user?.email || '-' }}
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'hierarchy'">
                        <div class="hierarchy-cell-compact">
                            <span class="hierarchy-compact-text">
                                <span
                                    v-if="record.node_hierarchy?.mission"
                                    class="hierarchy-compact-item mission clickable"
                                    @click.stop="
                                        handleHierarchyClick('mission', record)
                                    "
                                >
                                    {{ record.node_hierarchy.mission.name }}
                                </span>
                                <span
                                    v-if="record.node_hierarchy?.chapter"
                                    class="hierarchy-compact-item chapter clickable"
                                    @click.stop="
                                        handleHierarchyClick('chapter', record)
                                    "
                                >
                                    ›
                                    {{ record.node_hierarchy.chapter.name }}
                                </span>
                                <span
                                    v-if="record.node_hierarchy?.task"
                                    class="hierarchy-compact-item task clickable"
                                    @click.stop="
                                        handleHierarchyClick('task', record)
                                    "
                                >
                                    › {{ record.node_hierarchy.task.name }}
                                </span>
                                <span
                                    v-if="record.node_hierarchy?.step"
                                    class="hierarchy-compact-item step clickable"
                                    @click.stop="
                                        handleHierarchyClick('step', record)
                                    "
                                >
                                    › {{ record.node_hierarchy.step.name }}
                                </span>
                            </span>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'description'">
                        <div class="description-cell">
                            {{
                                getBottomNodeDescription(
                                    record.node_hierarchy
                                ) || '-'
                            }}
                        </div>
                    </template>
                    <template v-else-if="column.key === 'condition'">
                        <div class="condition-cell">
                            <div class="condition-content">
                                <a-tag
                                    :color="
                                        getConditionTypeColor(
                                            record.condition?.type
                                        )
                                    "
                                    class="condition-tag"
                                >
                                    {{
                                        getConditionTypeLabel(
                                            record.condition?.type
                                        )
                                    }}
                                </a-tag>
                                <span class="condition-name">
                                    {{
                                        getConditionDisplayText(
                                            record.condition,
                                            record.metadata
                                        )
                                    }}
                                </span>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'result_status'">
                        <div class="status-cell">
                            <a-tag
                                :color="getStatusColor(record.result_status)"
                            >
                                {{ getStatusLabel(record.result_status) }}
                            </a-tag>
                            <div
                                v-if="
                                    record.result_status === 'approved' ||
                                    record.result_status === 'rejected' ||
                                    record.result_status === 'completed' ||
                                    record.result_status === 'failed'
                                "
                                class="reviewer-info"
                            >
                                <div class="reviewer-name">
                                    <span class="reviewer-label">審核者：</span>
                                    <span class="reviewer-value">
                                        {{
                                            record.reviewer?.name ||
                                            `#${record.reviewed_by}` ||
                                            '-'
                                        }}
                                    </span>
                                </div>
                                <div
                                    v-if="record.reviewed_at"
                                    class="reviewer-time"
                                >
                                    <span class="reviewer-label">時間：</span>
                                    <span class="reviewer-value">
                                        {{ formatDateTime(record.reviewed_at) }}
                                    </span>
                                </div>
                                <div
                                    v-if="record.review_note"
                                    class="reviewer-note"
                                >
                                    <span class="reviewer-label">備註：</span>
                                    <span class="reviewer-value">
                                        {{ record.review_note }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else-if="column.key === 'submitted_at'">
                        {{ formatDateTime(record.submitted_at) || '-' }}
                    </template>
                    <template
                        v-else-if="
                            column.key === 'action' && hasAnyActionPermission
                        "
                    >
                        <a-space class="action-buttons">
                            <a-button
                                v-if="
                                    record.result_status !== 'completed' &&
                                    record.result_status !== 'failed' &&
                                    (canApproveReview || canRejectReview)
                                "
                                size="small"
                                class="action-btn-edit"
                                @click="handleReview(record)"
                            >
                                審核
                            </a-button>
                            <a-button
                                v-if="canViewReview"
                                size="small"
                                class="action-btn-view"
                                @click="handleViewDetail(record)"
                            >
                                查看
                            </a-button>
                        </a-space>
                    </template>
                </template>
            </DataTable>
        </div>

        <!-- 審核 Modal -->
        <a-modal
            v-model:open="reviewModalVisible"
            title="審核條件記錄"
            :width="900"
            :footer="null"
            class="review-modal vaporwave-modal"
            @cancel="handleReviewModalCancel"
        >
            <div v-if="currentReviewRecord" class="review-modal-content">
                <!-- 記錄資訊卡片 -->
                <div class="review-info-card">
                    <div class="info-section">
                        <div class="info-row">
                            <div class="info-label">會員</div>
                            <div class="info-value">
                                <span
                                    v-if="
                                        currentReviewRecord.user?.name &&
                                        currentReviewRecord.user?.email
                                    "
                                >
                                    {{ currentReviewRecord.user.name }}({{
                                        currentReviewRecord.user.email
                                    }})
                                </span>
                                <span
                                    v-else-if="currentReviewRecord.user?.name"
                                >
                                    {{ currentReviewRecord.user.name }}
                                </span>
                                <span
                                    v-else-if="currentReviewRecord.user?.email"
                                >
                                    {{ currentReviewRecord.user.email }}
                                </span>
                                <span v-else>-</span>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">條件</div>
                            <div class="info-value">
                                <div class="condition-info">
                                    <a-tag
                                        :color="
                                            getConditionTypeColor(
                                                currentReviewRecord.condition
                                                    ?.type
                                            )
                                        "
                                        class="condition-tag-inline"
                                    >
                                        {{
                                            getConditionTypeLabel(
                                                currentReviewRecord.condition
                                                    ?.type
                                            )
                                        }}
                                    </a-tag>
                                    <span class="condition-name-inline">
                                        {{
                                            getConditionDisplayText(
                                                currentReviewRecord.condition,
                                                currentReviewRecord.metadata
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">任務樹</div>
                            <div class="info-value">
                                <div class="hierarchy-path">
                                    <div
                                        v-if="
                                            currentReviewRecord.node_hierarchy
                                                ?.mission
                                        "
                                        class="hierarchy-item mission"
                                    >
                                        <a-tag size="small" color="purple">
                                            主線
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentReviewRecord
                                                    .node_hierarchy.mission.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentReviewRecord.node_hierarchy
                                                ?.chapter
                                        "
                                        class="hierarchy-item chapter"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="blue">
                                            章節
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentReviewRecord
                                                    .node_hierarchy.chapter.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentReviewRecord.node_hierarchy
                                                ?.task
                                        "
                                        class="hierarchy-item task"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="green">
                                            任務
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentReviewRecord
                                                    .node_hierarchy.task.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentReviewRecord.node_hierarchy
                                                ?.step
                                        "
                                        class="hierarchy-item step"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="orange">
                                            步驟
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentReviewRecord
                                                    .node_hierarchy.step.name
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">提交時間</div>
                            <div class="info-value">
                                {{
                                    formatDateTime(
                                        currentReviewRecord.submitted_at
                                    ) || '-'
                                }}
                            </div>
                        </div>

                        <div
                            class="info-row"
                            v-if="currentReviewRecord.metadata"
                        >
                            <div class="info-label">詳細資料</div>
                            <div class="info-value">
                                <pre class="metadata-display">{{
                                    JSON.stringify(
                                        currentReviewRecord.metadata,
                                        null,
                                        2
                                    )
                                }}</pre>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 審核表單 -->
                <div class="review-form-card">
                    <a-form
                        :model="reviewForm"
                        :rules="reviewFormRules"
                        ref="reviewFormRef"
                        layout="vertical"
                    >
                        <a-form-item label="審核結果" name="result_status">
                            <a-radio-group
                                v-model:value="reviewForm.result_status"
                                size="middle"
                            >
                                <a-radio-button value="approved">
                                    通過
                                </a-radio-button>
                                <a-radio-button value="rejected">
                                    拒絕
                                </a-radio-button>
                            </a-radio-group>
                        </a-form-item>

                        <a-form-item label="審核備註" name="review_note">
                            <a-textarea
                                v-model:value="reviewForm.review_note"
                                :rows="4"
                                :maxlength="2000"
                                placeholder="請輸入審核備註（選填，最多2000字元）"
                                show-count
                            />
                        </a-form-item>

                        <a-form-item>
                            <a-space>
                                <a-button
                                    class="vaporwave-btn review-submit-btn"
                                    size="middle"
                                    :loading="reviewSubmitting"
                                    @click="handleSubmitReview"
                                >
                                    <CheckCircleOutlined />
                                    提交審核
                                </a-button>
                                <a-button
                                    class="vaporwave-btn review-cancel-btn"
                                    size="middle"
                                    @click="handleReviewModalCancel"
                                >
                                    <CloseCircleOutlined />
                                    取消
                                </a-button>
                            </a-space>
                        </a-form-item>
                    </a-form>
                </div>
            </div>
        </a-modal>

        <!-- 查看詳情 Modal -->
        <a-modal
            v-model:open="detailModalVisible"
            title="查看審核詳情"
            :width="900"
            :footer="null"
            class="detail-modal"
            @cancel="handleDetailModalCancel"
        >
            <div v-if="currentDetailRecord" class="detail-modal-content">
                <!-- 記錄資訊卡片 -->
                <div class="review-info-card">
                    <div class="info-section">
                        <div class="info-row">
                            <div class="info-label">會員</div>
                            <div class="info-value">
                                <span
                                    v-if="
                                        currentDetailRecord.user?.name &&
                                        currentDetailRecord.user?.email
                                    "
                                >
                                    {{ currentDetailRecord.user.name }}({{
                                        currentDetailRecord.user.email
                                    }})
                                </span>
                                <span
                                    v-else-if="currentDetailRecord.user?.name"
                                >
                                    {{ currentDetailRecord.user.name }}
                                </span>
                                <span
                                    v-else-if="currentDetailRecord.user?.email"
                                >
                                    {{ currentDetailRecord.user.email }}
                                </span>
                                <span v-else>-</span>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">條件</div>
                            <div class="info-value">
                                <div class="condition-info">
                                    <a-tag
                                        :color="
                                            getConditionTypeColor(
                                                currentDetailRecord.condition
                                                    ?.type
                                            )
                                        "
                                        class="condition-tag-inline"
                                    >
                                        {{
                                            getConditionTypeLabel(
                                                currentDetailRecord.condition
                                                    ?.type
                                            )
                                        }}
                                    </a-tag>
                                    <span class="condition-name-inline">
                                        {{
                                            getConditionDisplayText(
                                                currentDetailRecord.condition,
                                                currentDetailRecord.metadata
                                            )
                                        }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">狀態</div>
                            <div class="info-value">
                                <a-tag
                                    :color="
                                        getStatusColor(
                                            currentDetailRecord.result_status
                                        )
                                    "
                                >
                                    {{
                                        getStatusLabel(
                                            currentDetailRecord.result_status
                                        )
                                    }}
                                </a-tag>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">任務樹</div>
                            <div class="info-value">
                                <div class="hierarchy-path">
                                    <div
                                        v-if="
                                            currentDetailRecord.node_hierarchy
                                                ?.mission
                                        "
                                        class="hierarchy-item mission"
                                    >
                                        <a-tag size="small" color="purple">
                                            主線
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentDetailRecord
                                                    .node_hierarchy.mission.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentDetailRecord.node_hierarchy
                                                ?.chapter
                                        "
                                        class="hierarchy-item chapter"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="blue">
                                            章節
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentDetailRecord
                                                    .node_hierarchy.chapter.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentDetailRecord.node_hierarchy
                                                ?.task
                                        "
                                        class="hierarchy-item task"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="green">
                                            任務
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentDetailRecord
                                                    .node_hierarchy.task.name
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            currentDetailRecord.node_hierarchy
                                                ?.step
                                        "
                                        class="hierarchy-item step"
                                    >
                                        <span class="hierarchy-separator">
                                            ›
                                        </span>
                                        <a-tag size="small" color="orange">
                                            步驟
                                        </a-tag>
                                        <span class="hierarchy-name">
                                            {{
                                                currentDetailRecord
                                                    .node_hierarchy.step.name
                                            }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="info-row">
                            <div class="info-label">提交時間</div>
                            <div class="info-value">
                                {{
                                    formatDateTime(
                                        currentDetailRecord.submitted_at
                                    ) || '-'
                                }}
                            </div>
                        </div>

                        <div
                            v-if="
                                currentDetailRecord.result_status !== 'pending'
                            "
                            class="info-row"
                        >
                            <div class="info-label">審核者</div>
                            <div class="info-value">
                                {{
                                    currentDetailRecord.reviewer?.name ||
                                    (currentDetailRecord.reviewed_by
                                        ? `#${currentDetailRecord.reviewed_by}`
                                        : '-')
                                }}
                            </div>
                        </div>

                        <div
                            v-if="
                                currentDetailRecord.reviewed_at &&
                                currentDetailRecord.result_status !== 'pending'
                            "
                            class="info-row"
                        >
                            <div class="info-label">審核時間</div>
                            <div class="info-value">
                                {{
                                    formatDateTime(
                                        currentDetailRecord.reviewed_at
                                    ) || '-'
                                }}
                            </div>
                        </div>

                        <div
                            v-if="
                                currentDetailRecord.review_note &&
                                currentDetailRecord.result_status !== 'pending'
                            "
                            class="info-row"
                        >
                            <div class="info-label">審核備註</div>
                            <div class="info-value">
                                {{ currentDetailRecord.review_note }}
                            </div>
                        </div>

                        <div
                            class="info-row"
                            v-if="currentDetailRecord.metadata"
                        >
                            <div class="info-label">詳細資料</div>
                            <div class="info-value">
                                <a-collapse
                                    v-model:activeKey="metadataCollapseKeys"
                                    :bordered="false"
                                >
                                    <a-collapse-panel
                                        key="translated"
                                        :showArrow="true"
                                    >
                                        <template #header>
                                            <span>翻譯後資料</span>
                                        </template>
                                        <a-descriptions
                                            :column="2"
                                            bordered
                                            size="small"
                                        >
                                            <template
                                                v-for="(
                                                    value, key
                                                ) in currentDetailRecord.metadata"
                                                :key="key"
                                            >
                                                <a-descriptions-item
                                                    :label="
                                                        formatMetadataLabel(key)
                                                    "
                                                >
                                                    <template
                                                        v-if="
                                                            key ===
                                                                'auto_verified' ||
                                                            key ===
                                                                'manual_completed'
                                                        "
                                                    >
                                                        {{
                                                            value ? '是' : '否'
                                                        }}
                                                    </template>
                                                    <template
                                                        v-else-if="
                                                            key.includes(
                                                                '_at'
                                                            ) ||
                                                            key.includes(
                                                                'time'
                                                            ) ||
                                                            key.includes('date')
                                                        "
                                                    >
                                                        {{
                                                            formatDateTime(
                                                                value
                                                            ) || value
                                                        }}
                                                    </template>
                                                    <template
                                                        v-else-if="
                                                            key === 'status'
                                                        "
                                                    >
                                                        {{
                                                            getMetadataStatusText(
                                                                value
                                                            )
                                                        }}
                                                    </template>
                                                    <template v-else>
                                                        {{ value }}
                                                    </template>
                                                </a-descriptions-item>
                                            </template>
                                        </a-descriptions>
                                    </a-collapse-panel>
                                    <a-collapse-panel
                                        key="raw"
                                        :showArrow="true"
                                    >
                                        <template #header>
                                            <span>原始 JSON</span>
                                        </template>
                                        <pre class="metadata-display">{{
                                            JSON.stringify(
                                                currentDetailRecord.metadata,
                                                null,
                                                2
                                            )
                                        }}</pre>
                                    </a-collapse-panel>
                                </a-collapse>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    FilterOutlined,
    SearchOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons-vue';
import DataTable from '@/components/common/DataTable.vue';
import {
    getConditionReviews,
    getConditionReviewDetail,
    getMissionDropdown,
    getChapterDropdown,
    getTaskDropdown,
    getStepDropdown,
    approveConditionReview,
    rejectConditionReview,
    batchReviewConditions
} from '@/api/missions';
import { searchUsers } from '@/api/users';
import { fetchAllRankRows, toRankOptions } from '@/api/ranks';
import { fetchAllDepartmentRows, toDepartmentOptions } from '@/api/departments';
import formatDateTime from '@/utils/formatDateTime';
import { useAdminInfo } from '@/stores/useAdminInfo';

const adminStore = useAdminInfo();
const route = useRoute();

// 權限檢查
const canViewReview = computed(() => adminStore.hasPermissionCode('130301')); // 查看審核
const canApproveReview = computed(() => adminStore.hasPermissionCode('130302')); // 核准審核
const canRejectReview = computed(() => adminStore.hasPermissionCode('130303')); // 拒絕審核
const canBatchReview = computed(() => adminStore.hasPermissionCode('130304')); // 批量審核

// 是否有任何操作權限
const hasAnyActionPermission = computed(() => {
    return (
        canApproveReview.value || canRejectReview.value || canViewReview.value
    );
});

const loading = ref(false);
const reviewList = ref([]);
const filters = reactive({
    user_id: undefined,
    condition_type: undefined,
    result_status: 'pending', // 預設顯示待審核
    rank_id: undefined,
    department_id: undefined,
    mission_id: undefined,
    chapter_id: undefined,
    task_id: undefined,
    step_id: undefined
});

// 批次選擇
const selectedRowKeys = ref([]);
const batchReviewLoading = ref(false);

// 篩選區塊展開/收合
const filterCollapseActiveKey = ref(['filters']);

// 選中的用戶資訊（用於顯示）
const selectedUser = ref(null);

// 階層式節點選擇路徑
const nodePath = reactive({
    missionId: undefined,
    chapterId: undefined,
    taskId: undefined,
    stepId: undefined
});

// 下拉選單資料
const missionOptions = ref([]);
const chapterOptions = ref([]);
const taskOptions = ref([]);
const stepOptions = ref([]);

// 聘階與事業部選項
const rankOptions = ref([]);
const departmentOptions = ref([]);
const rankLoading = ref(false);
const departmentLoading = ref(false);

// 載入狀態
const loadingMissions = ref(false);
const loadingChapters = ref(false);
const loadingTasks = ref(false);
const loadingSteps = ref(false);

// 審核 Modal
const reviewModalVisible = ref(false);
const currentReviewRecord = ref(null);
const reviewFormRef = ref(null);
const reviewSubmitting = ref(false);
const reviewForm = reactive({
    result_status: 'approved',
    review_note: ''
});
const reviewFormRules = {
    result_status: [
        { required: true, message: '請選擇審核結果', trigger: 'change' }
    ]
};

// 查看詳情 Modal
const detailModalVisible = ref(false);
const currentDetailRecord = ref(null);
const detailLoading = ref(false);
const metadataCollapseKeys = ref(['translated']);

// 選項
const userOptions = ref([]);
const userLoading = ref(false);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

// 條件類型選項
const conditionTypeOptions = [
    { label: '全部', value: undefined },
    { label: '表單', value: 'form' },
    { label: 'API 驗證', value: 'api_verify' },
    { label: '購買', value: 'purchase' },
    { label: '測驗通過', value: 'quiz_pass' }
];

// 審核狀態選項
const statusOptions = [
    { label: '全部', value: 'all' },
    { label: '待審核', value: 'pending' },
    { label: '已核准', value: 'approved' },
    { label: '已拒絕', value: 'rejected' },
    { label: '已完成', value: 'completed' },
    { label: '已失敗', value: 'failed' }
];

// 主線選項
const missionSelectOptions = computed(() =>
    missionOptions.value.map((mission) => ({
        label: mission.name || `未命名主線（ID: ${mission.node_id}）`,
        value: mission.node_id
    }))
);

// 章節選項
const chapterSelectOptions = computed(() =>
    chapterOptions.value.map((chapter) => ({
        label: chapter.name || `未命名章節（ID: ${chapter.node_id}）`,
        value: chapter.node_id
    }))
);

// 任務選項
const taskSelectOptions = computed(() =>
    taskOptions.value.map((task) => ({
        label: task.name || `未命名任務（ID: ${task.node_id}）`,
        value: task.node_id
    }))
);

// 步驟選項
const stepSelectOptions = computed(() =>
    stepOptions.value.map((step) => ({
        label: step.name || `未命名步驟（ID: ${step.node_id}）`,
        value: step.node_id
    }))
);

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 筆`
});

// 表格欄位
const columns = [
    {
        title: '會員',
        key: 'user_name',
        dataIndex: 'user',
        width: 150,
        align: 'left'
    },
    {
        title: 'Email',
        key: 'user_email',
        dataIndex: 'user',
        width: 200,
        align: 'left'
    },
    {
        title: '任務樹',
        key: 'hierarchy',
        dataIndex: 'hierarchy',
        width: 250,
        align: 'left'
    },
    {
        title: '說明',
        key: 'description',
        dataIndex: 'node_hierarchy',
        width: 250,
        align: 'left'
    },
    {
        title: '條件',
        key: 'condition',
        dataIndex: 'condition',
        width: 200,
        align: 'center'
    },
    {
        title: '狀態',
        key: 'result_status',
        dataIndex: 'result_status',
        width: 200,
        align: 'center'
    },
    {
        title: '提交時間',
        key: 'submitted_at',
        dataIndex: 'submitted_at',
        width: 180,
        align: 'center'
    },
    {
        title: '操作',
        key: 'action',
        width: 100,
        align: 'center',
        fixed: 'right'
    }
].filter((col) => {
    // 如果沒有操作權限，隱藏操作欄
    if (col.key === 'action' && !hasAnyActionPermission.value) {
        return false;
    }
    return true;
});

// 載入審核列表
const loadReviews = async () => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        if (filters.user_id) {
            params.user_id = filters.user_id;
        }
        if (filters.condition_type) {
            params.condition_type = filters.condition_type;
        }
        // 只有當狀態不是 "all" 時才傳遞 result_status
        if (filters.result_status && filters.result_status !== 'all') {
            params.result_status = filters.result_status;
        }
        if (filters.mission_id) {
            params.mission_id = filters.mission_id;
        }
        if (filters.chapter_id) {
            params.chapter_id = filters.chapter_id;
        }
        if (filters.task_id) {
            params.task_id = filters.task_id;
        }
        if (filters.step_id) {
            params.step_id = filters.step_id;
        }
        if (filters.rank_id) {
            params.rank_id = filters.rank_id;
        }
        if (filters.department_id) {
            params.department_id = filters.department_id;
        }

        const response = await getConditionReviews(params);

        if (response?.result) {
            reviewList.value = response.data?.data || [];
            pagination.total = response.data?.pagination?.total || 0;
            pagination.current = response.data?.pagination?.current_page || 1;
        } else {
            showErrorMessage(response?.message || '取得審核列表失敗');
            reviewList.value = [];
            pagination.total = 0;
        }
    } catch (error) {
        console.error('GET /api/admin/mission-conditions/reviews 失敗', error);
        showErrorMessage('取得審核列表失敗');
        reviewList.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

// 會員搜尋
const fetchUserOptions = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res?.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            userOptions.value = arr.map((u) => ({
                label: u.name || u.user_name || `#${u.id}`,
                value: u.id
            }));
        }
    } catch (e) {
        // 靜默失敗即可
    } finally {
        userLoading.value = false;
    }
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const onUserChange = (value) => {
    filters.user_id = value;
    // 保存選中的用戶資訊
    if (value) {
        const user = userOptions.value.find((u) => u.value === value);
        selectedUser.value = user ? { id: user.value, name: user.label } : null;
    } else {
        selectedUser.value = null;
    }
};

const onUserClear = () => {
    filters.user_id = undefined;
    selectedUser.value = null;
    userOptions.value = [];
};

// 查詢
const handleSearch = () => {
    pagination.current = 1;
    loadReviews();
};

// 載入聘階選單
const loadRanks = async () => {
    rankLoading.value = true;
    try {
        const rows = await fetchAllRankRows();
        rankOptions.value = toRankOptions(rows);
    } catch (error) {
        console.error('載入聘階選單失敗：', error);
    } finally {
        rankLoading.value = false;
    }
};

// 載入事業部選單
const loadDepartments = async () => {
    departmentLoading.value = true;
    try {
        const rows = await fetchAllDepartmentRows();
        departmentOptions.value = toDepartmentOptions(rows);
    } catch (error) {
        console.error('載入事業部選單失敗：', error);
    } finally {
        departmentLoading.value = false;
    }
};

// 載入主線列表
const loadMissions = async () => {
    try {
        loadingMissions.value = true;
        const response = await getMissionDropdown();
        if (response?.result) {
            missionOptions.value = response.data || [];
        } else {
            missionOptions.value = [];
        }
    } catch (error) {
        console.error('載入主線列表失敗', error);
        missionOptions.value = [];
    } finally {
        loadingMissions.value = false;
    }
};

// 載入章節
const loadChapters = async (missionId) => {
    if (!missionId) {
        chapterOptions.value = [];
        return;
    }

    try {
        loadingChapters.value = true;
        const response = await getChapterDropdown({ mission_id: missionId });
        if (response?.result) {
            chapterOptions.value = response.data || [];
        } else {
            chapterOptions.value = [];
        }
    } catch (error) {
        console.error('載入章節列表失敗', error);
        chapterOptions.value = [];
    } finally {
        loadingChapters.value = false;
    }
};

// 載入任務
const loadTasks = async (chapterId, missionId) => {
    if (!chapterId && !missionId) {
        taskOptions.value = [];
        return;
    }

    try {
        loadingTasks.value = true;
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;

        const response = await getTaskDropdown(params);
        if (response?.result) {
            taskOptions.value = response.data || [];
        } else {
            taskOptions.value = [];
        }
    } catch (error) {
        console.error('載入任務列表失敗', error);
        taskOptions.value = [];
    } finally {
        loadingTasks.value = false;
    }
};

// 載入步驟
const loadSteps = async (taskId, chapterId, missionId) => {
    if (!taskId && !chapterId && !missionId) {
        stepOptions.value = [];
        return;
    }

    try {
        loadingSteps.value = true;
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;
        if (taskId) params.task_id = taskId;

        const response = await getStepDropdown(params);
        if (response?.result) {
            stepOptions.value = response.data || [];
        } else {
            stepOptions.value = [];
        }
    } catch (error) {
        console.error('載入步驟列表失敗', error);
        stepOptions.value = [];
    } finally {
        loadingSteps.value = false;
    }
};

// 主線變更處理
const handleMissionChange = async (missionId) => {
    nodePath.missionId = missionId;
    nodePath.chapterId = undefined;
    nodePath.taskId = undefined;
    nodePath.stepId = undefined;

    // 清除下層選項
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (missionId) {
        await loadChapters(missionId);
    }
};

// 章節變更處理
const handleChapterChange = async (chapterId) => {
    nodePath.chapterId = chapterId;
    nodePath.taskId = undefined;
    nodePath.stepId = undefined;

    // 清除下層選項
    taskOptions.value = [];
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (chapterId) {
        await loadTasks(chapterId, nodePath.missionId);
    }
};

// 任務變更處理
const handleTaskChange = async (taskId) => {
    nodePath.taskId = taskId;
    nodePath.stepId = undefined;

    // 清除下層選項
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (taskId) {
        await loadSteps(taskId, nodePath.chapterId, nodePath.missionId);
    }
};

// 步驟變更處理
const handleStepChange = (stepId) => {
    nodePath.stepId = stepId;
    // 更新篩選條件
    updateNodeFilters();
};

// 更新節點篩選條件
const updateNodeFilters = () => {
    // 清除所有節點篩選條件
    filters.mission_id = undefined;
    filters.chapter_id = undefined;
    filters.task_id = undefined;
    filters.step_id = undefined;

    // 根據選擇的路徑設定篩選條件（只設定最底層的 ID）
    if (nodePath.stepId) {
        filters.step_id = nodePath.stepId;
    } else if (nodePath.taskId) {
        filters.task_id = nodePath.taskId;
    } else if (nodePath.chapterId) {
        filters.chapter_id = nodePath.chapterId;
    } else if (nodePath.missionId) {
        filters.mission_id = nodePath.missionId;
    }
};

// 清除節點路徑
const handleNodePathClear = () => {
    nodePath.missionId = undefined;
    nodePath.chapterId = undefined;
    nodePath.taskId = undefined;
    nodePath.stepId = undefined;
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];
    updateNodeFilters();
};

// 重置
const handleReset = () => {
    filters.user_id = undefined;
    filters.condition_type = undefined;
    filters.result_status = 'pending'; // 重置為待審核
    filters.rank_id = undefined;
    filters.department_id = undefined;
    filters.mission_id = undefined;
    filters.chapter_id = undefined;
    filters.task_id = undefined;
    filters.step_id = undefined;
    selectedUser.value = null;
    userOptions.value = [];
    selectedRowKeys.value = [];
    handleNodePathClear();
    pagination.current = 1;
    loadReviews();
};

// 選擇變更處理
const handleSelectionChange = (selectedKeys) => {
    selectedRowKeys.value = selectedKeys;
};

// 計算活躍的篩選條件數量
const getActiveFilterCount = () => {
    let count = 0;
    if (filters.user_id) count++;
    if (filters.condition_type) count++;
    if (filters.result_status && filters.result_status !== 'pending') count++;
    if (filters.rank_id) count++;
    if (filters.department_id) count++;
    if (filters.mission_id) count++;
    if (filters.chapter_id) count++;
    if (filters.task_id) count++;
    if (filters.step_id) count++;
    return count;
};

// 點擊會員名稱，帶入搜尋
const handleUserNameClick = async (record) => {
    if (record?.user?.id) {
        filters.user_id = record.user.id;
        // 設定選中的用戶資訊
        selectedUser.value = {
            id: record.user.id,
            name: record.user.name
        };
        // 更新 userOptions 以便顯示
        userOptions.value = [
            {
                label: record.user.name,
                value: record.user.id
            }
        ];
        pagination.current = 1;
        loadReviews();
    }
};

// 點擊任務樹，帶入對應的篩選條件
const handleHierarchyClick = async (type, record) => {
    if (!record || !record.node_hierarchy) return;

    const hierarchy = record.node_hierarchy;

    // 清除所有階層的篩選條件
    filters.mission_id = undefined;
    filters.chapter_id = undefined;
    filters.task_id = undefined;
    filters.step_id = undefined;

    // 清除 nodePath 和選項
    nodePath.missionId = undefined;
    nodePath.chapterId = undefined;
    nodePath.taskId = undefined;
    nodePath.stepId = undefined;
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];

    // 根據點擊的類型設定對應的篩選條件和完整階層
    switch (type) {
        case 'mission':
            if (hierarchy.mission?.node_id) {
                filters.mission_id = hierarchy.mission.node_id;
                nodePath.missionId = hierarchy.mission.node_id;
                // 載入章節選項以便顯示
                await loadChapters(hierarchy.mission.node_id);
            }
            break;
        case 'chapter':
            if (hierarchy.mission?.node_id) {
                filters.mission_id = hierarchy.mission.node_id;
                nodePath.missionId = hierarchy.mission.node_id;
                await loadChapters(hierarchy.mission.node_id);
            }
            if (hierarchy.chapter?.node_id) {
                filters.chapter_id = hierarchy.chapter.node_id;
                nodePath.chapterId = hierarchy.chapter.node_id;
                // 載入任務選項以便顯示
                await loadTasks(
                    hierarchy.chapter.node_id,
                    hierarchy.mission?.node_id
                );
            }
            break;
        case 'task':
            if (hierarchy.mission?.node_id) {
                filters.mission_id = hierarchy.mission.node_id;
                nodePath.missionId = hierarchy.mission.node_id;
                await loadChapters(hierarchy.mission.node_id);
            }
            if (hierarchy.chapter?.node_id) {
                filters.chapter_id = hierarchy.chapter.node_id;
                nodePath.chapterId = hierarchy.chapter.node_id;
                await loadTasks(
                    hierarchy.chapter.node_id,
                    hierarchy.mission?.node_id
                );
            }
            if (hierarchy.task?.node_id) {
                filters.task_id = hierarchy.task.node_id;
                nodePath.taskId = hierarchy.task.node_id;
                // 載入步驟選項以便顯示
                await loadSteps(
                    hierarchy.task.node_id,
                    hierarchy.chapter?.node_id,
                    hierarchy.mission?.node_id
                );
            }
            break;
        case 'step':
            if (hierarchy.mission?.node_id) {
                filters.mission_id = hierarchy.mission.node_id;
                nodePath.missionId = hierarchy.mission.node_id;
                await loadChapters(hierarchy.mission.node_id);
            }
            if (hierarchy.chapter?.node_id) {
                filters.chapter_id = hierarchy.chapter.node_id;
                nodePath.chapterId = hierarchy.chapter.node_id;
                await loadTasks(
                    hierarchy.chapter.node_id,
                    hierarchy.mission?.node_id
                );
            }
            if (hierarchy.task?.node_id) {
                filters.task_id = hierarchy.task.node_id;
                nodePath.taskId = hierarchy.task.node_id;
                await loadSteps(
                    hierarchy.task.node_id,
                    hierarchy.chapter?.node_id,
                    hierarchy.mission?.node_id
                );
            }
            if (hierarchy.step?.node_id) {
                filters.step_id = hierarchy.step.node_id;
                nodePath.stepId = hierarchy.step.node_id;
            }
            break;
    }

    pagination.current = 1;
    loadReviews();
};

// 表格變化處理
const handleTableChange = ({ pagination: pag }) => {
    if (pag) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
        loadReviews();
    }
};

// 審核
const handleReview = async (record) => {
    if (!record?.id) {
        showErrorMessage('無法取得記錄 ID');
        return;
    }

    if (!canApproveReview.value && !canRejectReview.value) {
        message.warning('您沒有審核權限');
        return;
    }

    try {
        reviewSubmitting.value = true;
        reviewModalVisible.value = true;
        const response = await getConditionReviewDetail(record.id);

        if (response?.result) {
            currentReviewRecord.value = response.data;
            // 如果已經審核過，預設選擇對應的狀態
            if (response.data.result_status === 'approved') {
                reviewForm.result_status = 'approved';
            } else if (response.data.result_status === 'rejected') {
                reviewForm.result_status = 'rejected';
            } else {
                reviewForm.result_status = 'approved';
            }
            reviewForm.review_note = response.data.review_note || '';
        } else {
            showErrorMessage(response?.message || '取得詳情失敗');
            reviewModalVisible.value = false;
        }
    } catch (error) {
        console.error('取得詳情失敗', error);
        showErrorMessage('取得詳情失敗');
        reviewModalVisible.value = false;
    } finally {
        reviewSubmitting.value = false;
    }
};

// 查看詳情
const handleViewDetail = async (record) => {
    if (!record?.id) {
        showErrorMessage('無法取得記錄 ID');
        return;
    }

    try {
        detailLoading.value = true;
        detailModalVisible.value = true;
        const response = await getConditionReviewDetail(record.id);

        if (response?.result) {
            currentDetailRecord.value = response.data;
        } else {
            showErrorMessage(response?.message || '取得詳情失敗');
            detailModalVisible.value = false;
        }
    } catch (error) {
        console.error('取得詳情失敗', error);
        showErrorMessage('取得詳情失敗');
        detailModalVisible.value = false;
    } finally {
        detailLoading.value = false;
    }
};

// 關閉詳情 Modal
const handleDetailModalCancel = () => {
    detailModalVisible.value = false;
    currentDetailRecord.value = null;
};

const handleReviewModalCancel = () => {
    reviewModalVisible.value = false;
    currentReviewRecord.value = null;
    reviewForm.result_status = 'approved';
    reviewForm.review_note = '';
};

const handleSubmitReview = async () => {
    try {
        await reviewFormRef.value.validate();
        reviewSubmitting.value = true;

        if (!currentReviewRecord.value?.id) {
            showErrorMessage('無法取得記錄 ID');
            return;
        }

        // 權限檢查
        if (
            reviewForm.result_status === 'approved' &&
            !canApproveReview.value
        ) {
            message.warning('您沒有核准審核的權限');
            reviewSubmitting.value = false;
            return;
        }

        if (reviewForm.result_status === 'rejected' && !canRejectReview.value) {
            message.warning('您沒有拒絕審核的權限');
            reviewSubmitting.value = false;
            return;
        }

        const reviewData = {
            note: reviewForm.review_note || undefined
        };

        let response;
        if (reviewForm.result_status === 'approved') {
            response = await approveConditionReview(
                currentReviewRecord.value.id,
                reviewData
            );
        } else {
            response = await rejectConditionReview(
                currentReviewRecord.value.id,
                reviewData
            );
        }

        if (response?.result) {
            message.success('審核成功');
            reviewModalVisible.value = false;
            loadReviews();
        } else {
            showErrorMessage(response?.message || '審核失敗');
        }
    } catch (error) {
        console.error('審核失敗', error);
        if (error?.errorFields) {
            return;
        }
        showErrorMessage('審核失敗');
    } finally {
        reviewSubmitting.value = false;
    }
};

// 批次審核通過
const handleBatchApprove = async () => {
    if (selectedRowKeys.value.length === 0) {
        message.warning('請選擇要審核的記錄');
        return;
    }

    if (!canBatchReview.value || !canApproveReview.value) {
        message.warning('您沒有批量核准審核的權限');
        return;
    }

    try {
        batchReviewLoading.value = true;
        const response = await batchReviewConditions({
            condition_ids: selectedRowKeys.value,
            action: 'approve',
            note: '批次審核通過'
        });

        if (response?.result) {
            message.success(`成功審核 ${selectedRowKeys.value.length} 筆記錄`);
            // 先清除選擇，避免狀態更新時的衝突
            selectedRowKeys.value = [];
            // 等待一下再載入，確保後端狀態已更新
            await nextTick();
            await loadReviews();
        } else {
            showErrorMessage(response?.message || '批次審核失敗');
        }
    } catch (error) {
        console.error('批次審核失敗', error);
        showErrorMessage('批次審核失敗');
    } finally {
        batchReviewLoading.value = false;
    }
};

// 批次審核拒絕
const handleBatchReject = async () => {
    if (selectedRowKeys.value.length === 0) {
        message.warning('請選擇要審核的記錄');
        return;
    }

    if (!canBatchReview.value || !canRejectReview.value) {
        message.warning('您沒有批量拒絕審核的權限');
        return;
    }

    try {
        batchReviewLoading.value = true;
        const response = await batchReviewConditions({
            condition_ids: selectedRowKeys.value,
            action: 'reject',
            note: '批次審核拒絕'
        });

        if (response?.result) {
            message.success(`成功審核 ${selectedRowKeys.value.length} 筆記錄`);
            // 先清除選擇，避免狀態更新時的衝突
            selectedRowKeys.value = [];
            // 等待一下再載入，確保後端狀態已更新
            await nextTick();
            await loadReviews();
        } else {
            showErrorMessage(response?.message || '批次審核失敗');
        }
    } catch (error) {
        console.error('批次審核失敗', error);
        showErrorMessage('批次審核失敗');
    } finally {
        batchReviewLoading.value = false;
    }
};

// 工具函數
const getConditionTypeColor = (type) => {
    const colorMap = {
        form: 'blue',
        api_verify: 'purple',
        purchase: 'green',
        quiz_pass: 'orange',
        ack: 'magenta'
    };
    return colorMap[type] || 'default';
};

const getConditionTypeLabel = (type) => {
    const labelMap = {
        form: '表單',
        api_verify: 'API 驗證',
        purchase: '購買',
        quiz_pass: '測驗通過',
        ack: '確認'
    };
    return labelMap[type] || type || '-';
};

// 取得條件顯示文字（處理 quiz_pass 等特殊格式）
const getConditionDisplayText = (condition, metadata) => {
    if (!condition) return '';

    // 如果有 code_name，優先使用
    if (condition.code_name) {
        return condition.code_name;
    }

    // 如果是 quiz_pass 類型，從 metadata 取得測驗名稱，顯示為「測驗通過：測驗名稱」
    if (condition.type === 'quiz_pass' && metadata) {
        const quizName =
            metadata.quiz_name ||
            (metadata.quiz_id ? `測驗 ID: ${metadata.quiz_id}` : '');
        if (quizName) {
            return `：${quizName}`;
        }
    }

    // 如果是 purchase 類型，從 metadata 取得商品名稱
    if (condition.type === 'purchase' && metadata) {
        const productName =
            metadata.product_name ||
            (metadata.product_id ? `商品 ID: ${metadata.product_id}` : '');
        if (productName) {
            return `：${productName}`;
        }
    }

    // 否則顯示 code（移除後綴數字）
    if (condition.code) {
        return condition.code.replace(/_\d+$/, '');
    }

    return '';
};

// 狀態顏色
const getStatusColor = (status) => {
    const colorMap = {
        pending: 'orange',
        approved: 'green',
        rejected: 'red',
        completed: 'blue',
        failed: 'volcano'
    };
    return colorMap[status] || 'default';
};

// 狀態標籤
const getStatusLabel = (status) => {
    const labelMap = {
        pending: '待審核',
        approved: '已核准',
        rejected: '已拒絕',
        completed: '已完成',
        failed: '已失敗'
    };
    return labelMap[status] || status || '-';
};

// 格式化 metadata 標籤
const formatMetadataLabel = (key) => {
    const labelMap = {
        status: '狀態',
        quiz_id: '測驗 ID',
        quiz_name: '測驗名稱',
        attempt_id: '作答紀錄 ID',
        total_score: '總分數',
        verified_at: '驗證時間',
        auto_verified: '自動驗證',
        manual_completed: '手動完成',
        manual_completed_at: '手動完成時間',
        manual_completed_by: '手動完成人員 ID',
        manual_completed_by_name: '手動完成人員',
        manual_completed_note: '手動完成備註',
        product_id: '商品 ID',
        product_name: '商品名稱',
        order_id: '訂單 ID',
        order_no: '訂單編號'
    };
    return (
        labelMap[key] ||
        key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
};

// 取得 metadata 狀態文字
const getMetadataStatusText = (status) => {
    const statusMap = {
        passed: '已通過',
        failed: '已失敗',
        pending: '待處理',
        completed: '已完成',
        in_progress: '進行中'
    };
    return statusMap[status] || status || '-';
};

// 取得最底層節點的說明
const getBottomNodeDescription = (nodeHierarchy) => {
    if (!nodeHierarchy) return null;
    // 優先順序：step > task > chapter > mission
    if (nodeHierarchy.step?.description) {
        return nodeHierarchy.step.description;
    }
    if (nodeHierarchy.task?.description) {
        return nodeHierarchy.task.description;
    }
    if (nodeHierarchy.chapter?.description) {
        return nodeHierarchy.chapter.description;
    }
    if (nodeHierarchy.mission?.description) {
        return nodeHierarchy.mission.description;
    }
    return null;
};

// 從 URL query 參數初始化篩選條件
const initFiltersFromQuery = async () => {
    const query = route.query;
    // 優先使用 user_name，如果沒有則使用 user_id
    if (query.user_name) {
        // 根據用戶名稱搜尋用戶
        try {
            userLoading.value = true;
            const res = await searchUsers({
                name: query.user_name.trim(),
                limit: 10
            });
            if (res?.result) {
                const arr = Array.isArray(res.data)
                    ? res.data
                    : res.data?.data || [];
                // 找到完全匹配的用戶
                const matchedUser = arr.find(
                    (u) => u.name === query.user_name.trim()
                );
                if (matchedUser) {
                    filters.user_id = matchedUser.id;
                    userOptions.value = [
                        {
                            label: matchedUser.name || `#${matchedUser.id}`,
                            value: matchedUser.id
                        }
                    ];
                    selectedUser.value = {
                        id: matchedUser.id,
                        name: matchedUser.name || `#${matchedUser.id}`
                    };
                } else if (arr.length > 0) {
                    // 如果沒有完全匹配，使用第一個結果
                    const firstUser = arr[0];
                    filters.user_id = firstUser.id;
                    userOptions.value = [
                        {
                            label: firstUser.name || `#${firstUser.id}`,
                            value: firstUser.id
                        }
                    ];
                    selectedUser.value = {
                        id: firstUser.id,
                        name: firstUser.name || `#${firstUser.id}`
                    };
                }
            }
        } catch (error) {
            console.error('根據用戶名稱搜尋失敗', error);
        } finally {
            userLoading.value = false;
        }
    } else if (query.user_id) {
        const userId = Number(query.user_id);
        if (!isNaN(userId)) {
            filters.user_id = userId;
            // 設定用戶選項（名稱會在列表載入後從記錄中取得）
            userOptions.value = [
                {
                    label: `用戶 #${userId}`,
                    value: userId
                }
            ];
            selectedUser.value = {
                id: userId,
                name: `用戶 #${userId}`
            };
        }
    }
    if (query.result_status) {
        filters.result_status = query.result_status;
    }
    if (query.rank_id) {
        const rankId = Number(query.rank_id);
        if (!isNaN(rankId)) filters.rank_id = rankId;
    }
    if (query.department_id) {
        const departmentId = Number(query.department_id);
        if (!isNaN(departmentId)) filters.department_id = departmentId;
    }
    if (query.mission_id) {
        const missionId = Number(query.mission_id);
        if (!isNaN(missionId)) {
            filters.mission_id = missionId;
            nodePath.missionId = missionId;
        }
    }
    if (query.chapter_id) {
        const chapterId = Number(query.chapter_id);
        if (!isNaN(chapterId)) {
            filters.chapter_id = chapterId;
            nodePath.chapterId = chapterId;
        }
    }
    if (query.task_id) {
        const taskId = Number(query.task_id);
        if (!isNaN(taskId)) {
            filters.task_id = taskId;
            nodePath.taskId = taskId;
        }
    }
    if (query.step_id) {
        const stepId = Number(query.step_id);
        if (!isNaN(stepId)) {
            filters.step_id = stepId;
            nodePath.stepId = stepId;
        }
    }
};

// 監聽 route 變化，當 URL query 改變時重新初始化篩選條件
watch(
    () => route.query,
    async () => {
        await initFiltersFromQuery();
        // 重新載入下拉選單和列表
        if (filters.mission_id) {
            await loadChapters(filters.mission_id);
            if (filters.chapter_id) {
                await loadTasks(filters.chapter_id, filters.mission_id);
                if (filters.task_id) {
                    await loadSteps(
                        filters.task_id,
                        filters.chapter_id,
                        filters.mission_id
                    );
                }
            }
        }
        loadReviews();
    },
    { immediate: false }
);

onMounted(async () => {
    // 先從 URL query 初始化篩選條件（async）
    await initFiltersFromQuery();
    await Promise.all([loadMissions(), loadRanks(), loadDepartments()]);
    // 如果有 mission_id，載入對應的章節選項
    if (filters.mission_id) {
        await loadChapters(filters.mission_id);
    }
    // 如果有 chapter_id，載入對應的任務選項
    if (filters.chapter_id && filters.mission_id) {
        await loadTasks(filters.chapter_id, filters.mission_id);
    }
    // 如果有 task_id，載入對應的步驟選項
    if (filters.task_id && filters.chapter_id && filters.mission_id) {
        await loadSteps(
            filters.task_id,
            filters.chapter_id,
            filters.mission_id
        );
    }
    // 確保在初始化篩選條件後載入列表
    loadReviews();
});
</script>

<style scoped>
.page {
    padding: 32px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95),
        rgba(255, 255, 255, 0.98)
    );
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
}

/* 背景裝飾網格 */
.page::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        linear-gradient(rgba(42, 74, 122, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(42, 74, 122, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
}

.page-header {
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    z-index: 1;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

.toolbar {
    margin-bottom: 32px;
    position: relative;
    z-index: 2;
}

/* Collapse 樣式 */
.filter-collapse {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 16px;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    overflow: hidden;
}

.filter-collapse :deep(.ant-collapse-item) {
    border: none;
    background: transparent;
}

.filter-collapse :deep(.ant-collapse-header) {
    padding: 16px 24px;
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.1);
}

.filter-collapse :deep(.ant-collapse-content) {
    background: transparent;
    border: none;
}

.filter-collapse :deep(.ant-collapse-content-box) {
    padding: 24px;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.filter-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #2a2a3a;
    font-size: 15px;
}

.filters-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.filter-group-title {
    font-size: 13px;
    font-weight: 600;
    color: #2a4a7a;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
}

.filter-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

.filter-item {
    flex: 0 0 auto;
    min-width: 160px;
    max-width: 220px;
}

.toolbar-actions {
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
}

/* 響應式調整 */
@media (max-width: 1400px) {
    .filter-item {
        min-width: 140px;
        max-width: 180px;
    }
}

@media (max-width: 1200px) {
    .filter-row {
        gap: 12px;
    }

    .filter-item {
        min-width: 130px;
        max-width: 160px;
    }
}

.filters {
    flex: 1;
    min-width: 300px;
}

/* 蒸氣波風格按鈕 */
.vaporwave-btn {
    border-radius: 10px;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border: none;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.3),
        inset 0 0 12px rgba(255, 255, 255, 0.1);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.vaporwave-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    transition: left 0.5s ease;
}

.vaporwave-btn:hover::before {
    left: 100%;
}

.vaporwave-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow:
        0 6px 24px rgba(42, 74, 122, 0.5),
        inset 0 0 15px rgba(255, 255, 255, 0.15);
}

.vaporwave-btn:active {
    transform: translateY(0) scale(1);
    box-shadow:
        0 2px 10px rgba(42, 74, 122, 0.4),
        inset 0 0 8px rgba(255, 255, 255, 0.1);
}

/* 批次通過按鈕 */
.batch-approve-btn {
    background: linear-gradient(45deg, #52c41a, #73d13d) !important;
    color: #ffffff !important;
}

.batch-approve-btn:hover {
    background: linear-gradient(45deg, #389e0d, #52c41a) !important;
}

/* 批次拒絕按鈕 */
.batch-reject-btn {
    background: linear-gradient(45deg, #ff4d4f, #ff7875) !important;
    color: #ffffff !important;
}

.batch-reject-btn:hover {
    background: linear-gradient(45deg, #cf1322, #ff4d4f) !important;
}

/* 查詢按鈕 */
.search-btn {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #ffffff !important;
}

.search-btn:hover {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
}

/* 重置按鈕 */
.reset-btn {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    color: #2a2a3a !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
}

.reset-btn:hover {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    ) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.user-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.user-option .user-name {
    font-weight: 500;
}

.user-id {
    color: #999;
    font-size: 12px;
}

.user-cell {
    text-align: left;
}

.user-cell .user-name {
    font-weight: 500;
    line-height: 1.5;
    color: #2a4a7a;
}

.user-cell .user-email {
    color: #3b6ea5;
    font-size: 13px;
    line-height: 1.5;
}

.user-name.clickable {
    color: #2a4a7a;
    cursor: pointer;
    transition: all 0.2s ease;
}

.user-name.clickable:hover {
    color: #64748b;
    text-decoration: underline;
}

.condition-cell {
    text-align: left;
}

.condition-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.condition-tag {
    flex-shrink: 0;
}

.condition-tag :deep(.ant-tag) {
    color: #ffffff !important;
    font-weight: 600;
}

.condition-tag :deep(.ant-tag-magenta) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.condition-name {
    font-size: 13px;
    color: #2a2a3a;
    line-height: 1.5;
    font-weight: 500;
    display: inline-block;
}

.hierarchy-cell-compact {
    text-align: left;
    padding: 4px 0;
}

.hierarchy-compact-text {
    display: inline-block;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
}

.hierarchy-compact-item {
    display: inline;
    font-weight: 500;
}

.hierarchy-compact-item.clickable {
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 2px 4px;
    border-radius: 4px;
}

.hierarchy-compact-item.clickable:hover {
    background-color: rgba(42, 74, 122, 0.1);
    text-decoration: underline;
}

.hierarchy-compact-item.mission {
    color: #2a4a7a;
}

.hierarchy-compact-item.chapter {
    color: #3b6ea5;
}

.hierarchy-compact-item.task {
    color: #52c41a;
}

.hierarchy-compact-item.step {
    color: #ffa87a;
}

.description-cell {
    font-size: 13px;
    color: #2a2a3a;
    line-height: 1.5;
    white-space: normal;
    word-break: break-word;
    max-width: 100%;
    text-align: left;
    font-weight: 400;
}

.hierarchy-description {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.4;
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    white-space: normal;
    word-break: break-word;
    max-width: 100%;
}

/* Tooltip 樣式 */
:deep(.hierarchy-tooltip) {
    max-width: 300px;
}

.hierarchy-tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
}

.tooltip-item {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.6;
}

.tooltip-label {
    font-weight: 600;
    color: #2a4a7a;
    flex-shrink: 0;
    min-width: 50px;
}

.tooltip-value {
    color: #2a2a3a;
    word-break: break-word;
}

.tooltip-description {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    line-height: 1.6;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(42, 42, 58, 0.1);
}

.tooltip-description .tooltip-label {
    flex-shrink: 0;
    color: #6a6a7a;
    font-weight: 600;
    min-width: 50px;
}

.tooltip-description .tooltip-value {
    flex: 1;
    color: #2a2a3a;
    font-weight: 400;
    font-style: italic;
}

/* 保留 Modal 中的完整顯示樣式 */
.hierarchy-cell {
    text-align: left;
    padding: 8px 0;
}

.hierarchy-path {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    line-height: 1.8;
}

.hierarchy-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    padding: 4px 0;
}

.hierarchy-name {
    font-weight: 500;
    white-space: nowrap;
}

.hierarchy-separator {
    color: #2a4a7a;
    font-weight: bold;
    font-size: 18px;
    margin: 0 4px;
    line-height: 1;
}

.hierarchy-item.mission .hierarchy-name {
    color: #2a4a7a;
}

.hierarchy-item.chapter .hierarchy-name {
    color: #3b6ea5;
}

.hierarchy-item.task .hierarchy-name {
    color: #52c41a;
}

.hierarchy-item.step .hierarchy-name {
    color: #ffa87a;
}

/* 審核 Modal 樣式 */
:deep(.vaporwave-modal) {
    border-radius: 20px;
    overflow: hidden;
}

:deep(.vaporwave-modal .ant-modal-content) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 2px solid rgba(42, 74, 122, 0.2);
    box-shadow:
        0 8px 32px rgba(42, 74, 122, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
}

:deep(.vaporwave-modal .ant-modal-header) {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    );
    border-bottom: 1px solid rgba(42, 74, 122, 0.2);
    padding: 20px 24px;
}

:deep(.vaporwave-modal .ant-modal-title) {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    font-size: 20px;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-shadow: 0 0 20px rgba(42, 74, 122, 0.3);
}

:deep(.vaporwave-modal .ant-modal-close) {
    color: #2a4a7a;
    transition: all 0.2s ease;
}

:deep(.vaporwave-modal .ant-modal-close:hover) {
    color: #3b6ea5;
    transform: scale(1.1);
}

.review-modal-content {
    padding: 8px 0;
}

.review-info-card {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.15);
    border-radius: 16px;
    padding: 28px;
    margin-bottom: 28px;
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.08);
    position: relative;
    overflow: hidden;
}

.review-info-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #64748b, #2a4a7a, #3b6ea5);
    opacity: 0.6;
}

.info-section {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.info-row {
    display: flex;
    align-items: flex-start;
    padding: 12px 0;
    border-bottom: 1px solid rgba(42, 74, 122, 0.1);
}

.info-row:last-of-type {
    border-bottom: none;
}

.info-label {
    width: 100px;
    font-weight: 600;
    color: #666;
    flex-shrink: 0;
    font-size: 14px;
}

.info-value {
    flex: 1;
    color: #2a2a3a;
    font-size: 14px;
    line-height: 1.6;
}

.info-value .hierarchy-path {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 16px;
    line-height: 2;
}

.info-value .hierarchy-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    padding: 4px 0;
}

.info-value .hierarchy-name {
    font-weight: 500;
}

.info-value .hierarchy-separator {
    color: #2a4a7a;
    font-weight: bold;
    font-size: 20px;
    margin: 0 4px;
    line-height: 1;
}

.condition-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.condition-tag-inline {
    flex-shrink: 0;
}

.condition-tag-inline :deep(.ant-tag) {
    color: #ffffff !important;
    font-weight: 600;
}

.condition-tag-inline :deep(.ant-tag-magenta) {
    background: rgba(42, 74, 122, 0.2) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

.condition-name-inline {
    font-weight: 500;
    color: #ffffff;
}

.metadata-display {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    padding: 12px;
    font-size: 12px;
    max-height: 200px;
    overflow-y: auto;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-all;
}

.review-form-card {
    padding: 28px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border-radius: 16px;
    border: 1px solid rgba(42, 74, 122, 0.15);
    box-shadow: 0 2px 12px rgba(42, 74, 122, 0.08);
    position: relative;
    overflow: hidden;
}

.review-form-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #64748b, #2a4a7a, #3b6ea5);
    opacity: 0.6;
}

.review-form-card :deep(.ant-form-item-label > label) {
    font-weight: 600;
    color: #2a2a3a;
    font-size: 15px;
    font-family: 'Courier New', monospace;
}

.review-form-card :deep(.ant-radio-group) {
    width: 100%;
}

.review-form-card :deep(.ant-radio-button-wrapper) {
    flex: 1;
    text-align: center;
    height: 32px;
    line-height: 30px;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Courier New', monospace;
    border-radius: 8px !important;
    transition: all 0.3s ease;
    border: 1.5px solid rgba(42, 74, 122, 0.3);
    margin-right: 8px;
}

.review-form-card :deep(.ant-radio-button-wrapper:first-child) {
    border-top-left-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
    border-top-right-radius: 10px !important;
    border-bottom-right-radius: 10px !important;
}

.review-form-card :deep(.ant-radio-button-wrapper:last-child) {
    border-top-right-radius: 10px !important;
    border-bottom-right-radius: 10px !important;
    border-top-left-radius: 10px !important;
    border-bottom-left-radius: 10px !important;
    margin-right: 0;
}

.review-form-card :deep(.ant-radio-button-wrapper-checked) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border-color: #2a4a7a !important;
    color: #fff !important;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.4),
        inset 0 0 12px rgba(255, 255, 255, 0.1);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.review-form-card :deep(.ant-radio-button-wrapper-checked:hover) {
    background: linear-gradient(45deg, #b06dff, #3b6ea5) !important;
    border-color: #b06dff !important;
    transform: translateY(-2px);
    box-shadow:
        0 6px 24px rgba(42, 74, 122, 0.6),
        inset 0 0 15px rgba(255, 255, 255, 0.15);
}

.review-form-card
    :deep(
        .ant-radio-button-wrapper:not(.ant-radio-button-wrapper-checked):hover
    ) {
    border-color: #2a4a7a;
    background: rgba(42, 74, 122, 0.05);
}

.review-form-card :deep(.ant-input),
.review-form-card :deep(.ant-input:focus),
.review-form-card :deep(.ant-input-focused) {
    border-color: rgba(42, 74, 122, 0.3);
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.1);
}

.review-form-card :deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.5);
}

.review-submit-btn {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    color: #fff !important;
}

.review-submit-btn:hover {
    background: linear-gradient(45deg, #b06dff, #3b6ea5) !important;
}

.review-cancel-btn {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    color: #2a2a3a !important;
}

.review-cancel-btn:hover {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    ) !important;
    border-color: #2a4a7a !important;
    color: #2a4a7a !important;
}

/* 狀態欄位樣式 */
.status-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
}

.reviewer-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 8px;
    padding: 8px;
    background: rgba(42, 74, 122, 0.05);
    border-radius: 6px;
    font-size: 12px;
    text-align: left;
    min-width: 180px;
}

.reviewer-name,
.reviewer-time,
.reviewer-note {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    line-height: 1.5;
}

.reviewer-label {
    color: #666;
    font-weight: 500;
    flex-shrink: 0;
    min-width: 50px;
}

.reviewer-value {
    color: #2a2a3a;
    word-break: break-word;
}

.reviewer-note .reviewer-value {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 操作按鈕樣式 */
.action-buttons {
    display: flex;
    gap: 4px;
}

/* 統一篩選欄位樣式 */
.filters-content :deep(.status-select),
.filters-content :deep(.status-select .ant-select-selector) {
    height: 32px;
    font-size: 13px;
    border-radius: 8px;
    border: 1.5px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.95);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.filters-content :deep(.status-select:hover),
.filters-content :deep(.status-select.ant-select:hover .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.6);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.filters-content :deep(.status-select.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8);
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2);
}

/* 確保查詢和重置按鈕樣式優先 */
.toolbar-actions .unified-search-btn,
.toolbar-actions .unified-search-btn.ant-btn,
.toolbar-actions .unified-search-btn.ant-btn-default,
.toolbar-actions :deep(.unified-search-btn),
.toolbar-actions :deep(.unified-search-btn.ant-btn),
.toolbar-actions :deep(.unified-search-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.toolbar-actions .unified-search-btn:hover,
.toolbar-actions .unified-search-btn.ant-btn:hover,
.toolbar-actions .unified-search-btn.ant-btn-default:hover,
.toolbar-actions :deep(.unified-search-btn:hover),
.toolbar-actions :deep(.unified-search-btn.ant-btn:hover),
.toolbar-actions :deep(.unified-search-btn.ant-btn-default:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.toolbar-actions .unified-reset-btn,
.toolbar-actions .unified-reset-btn.ant-btn,
.toolbar-actions .unified-reset-btn.ant-btn-default,
.toolbar-actions :deep(.unified-reset-btn),
.toolbar-actions :deep(.unified-reset-btn.ant-btn),
.toolbar-actions :deep(.unified-reset-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #c4a5a5 !important;
    color: #c4a5a5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.toolbar-actions .unified-reset-btn:hover,
.toolbar-actions .unified-reset-btn.ant-btn:hover,
.toolbar-actions .unified-reset-btn.ant-btn-default:hover,
.toolbar-actions :deep(.unified-reset-btn:hover),
.toolbar-actions :deep(.unified-reset-btn.ant-btn:hover),
.toolbar-actions :deep(.unified-reset-btn.ant-btn-default:hover) {
    background: rgba(196, 165, 165, 0.1) !important;
    border-color: #c4a5a5 !important;
    color: #c4a5a5 !important;
    box-shadow: 0 2px 8px rgba(196, 165, 165, 0.3) !important;
}
</style>
